import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { RefreshCw, ShoppingCart, Zap, Clock, ExternalLink, Play, Settings, TrendingDown, Star, Package, Edit, Link2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AmazonDeals = () => {
  const [status, setStatus] = useState(null);
  const [deals, setDeals] = useState([]);
  const [published, setPublished] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchKeywords, setSearchKeywords] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Electronics');
  const [minSaving, setMinSaving] = useState(10);
  
  // Mode saisie manuelle
  const [manualUrl, setManualUrl] = useState('');
  const [manualProduct, setManualProduct] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [sites, setSites] = useState([]);
  
  // Mode: 'url' (essaie de scraper) ou 'full_manual' (saisie 100% manuelle)
  const [inputMode, setInputMode] = useState('url');
  
  // Formulaire de saisie manuelle (si scraping échoue OU mode full_manual)
  const [manualForm, setManualForm] = useState({
    title: '',
    price: '',
    originalPrice: '',
    imageUrl: '',
    brand: '',
    features: '',  // Caractéristiques du produit (séparées par des lignes)
    asin: ''       // ASIN pour le lien affilié
  });
  const [showManualForm, setShowManualForm] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/amazon/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching Amazon status:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/amazon/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        category: selectedCategory,
        min_saving: minSaving,
        max_results: 20
      };
      if (searchKeywords.trim()) {
        params.keywords = searchKeywords.trim();
      }
      const response = await axios.get(`${API}/api/amazon/deals`, { params });
      setDeals(response.data.products || []);
      toast.success(`${response.data.count} produits trouvés`);
    } catch (error) {
      toast.error('Erreur lors de la recherche');
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, minSaving, searchKeywords]);

  const fetchPublished = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/amazon/published`, { params: { limit: 30 } });
      setPublished(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching published:', error);
    }
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/wordpress-sites`);
      setSites(response.data || []);
      // Sélectionner le premier site par défaut
      if (response.data?.length > 0 && !selectedSiteId) {
        setSelectedSiteId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  }, [selectedSiteId]);

  useEffect(() => {
    fetchStatus();
    fetchCategories();
    fetchPublished();
    fetchSites();
  }, [fetchStatus, fetchCategories, fetchPublished, fetchSites]);

  // Récupérer les infos d'un produit depuis une URL
  const handleFetchProduct = async () => {
    if (!manualUrl.trim()) {
      toast.error('Entrez une URL Amazon ou un ASIN');
      return;
    }
    
    setLoading(true);
    setManualProduct(null);
    setShowManualForm(false);
    
    try {
      const payload = {
        url: manualUrl.trim(),
        site_id: selectedSiteId,
        publish_immediately: false
      };
      
      // Si formulaire manuel rempli, l'inclure
      if (manualForm.title) {
        payload.manual_title = manualForm.title;
        payload.manual_price = manualForm.price ? parseFloat(manualForm.price) : null;
        payload.manual_original_price = manualForm.originalPrice ? parseFloat(manualForm.originalPrice) : null;
        payload.manual_image_url = manualForm.imageUrl || null;
        payload.manual_brand = manualForm.brand || null;
      }
      
      const response = await axios.post(`${API}/api/amazon/from-url`, payload);
      
      if (response.data.product) {
        const product = response.data.product;
        setManualProduct(product);
        
        // Si produit minimal (scraping échoué), proposer saisie manuelle
        if (product.source === 'minimal') {
          toast.warning('Scraping bloqué par Amazon. Vous pouvez saisir les infos manuellement.');
          setShowManualForm(true);
        } else {
          toast.success('Produit trouvé !');
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur lors de la récupération';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // Appliquer les infos manuelles et re-fetch
  const handleApplyManualInfo = async () => {
    if (!manualForm.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/amazon/from-url`, {
        url: manualUrl.trim(),
        site_id: selectedSiteId,
        publish_immediately: false,
        manual_title: manualForm.title,
        manual_price: manualForm.price ? parseFloat(manualForm.price) : null,
        manual_original_price: manualForm.originalPrice ? parseFloat(manualForm.originalPrice) : null,
        manual_image_url: manualForm.imageUrl || null,
        manual_brand: manualForm.brand || null
      });
      
      if (response.data.product) {
        setManualProduct(response.data.product);
        setShowManualForm(false);
        toast.success('Informations mises à jour !');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  // Mode 100% manuel : générer et publier directement depuis le formulaire
  const handleFullManualGenerate = async () => {
    if (!manualForm.title.trim()) {
      toast.error('Le titre du produit est obligatoire');
      return;
    }
    if (!manualForm.price) {
      toast.error('Le prix est obligatoire');
      return;
    }
    if (!manualForm.asin.trim()) {
      toast.error('L\'ASIN ou l\'URL Amazon est obligatoire');
      return;
    }
    if (!selectedSiteId) {
      toast.error('Sélectionnez un site de destination');
      return;
    }
    
    setLoading(true);
    
    try {
      // Extraire l'ASIN si c'est une URL
      let asinValue = manualForm.asin.trim();
      // Si ça ressemble à une URL, extraire l'ASIN
      const asinMatch = asinValue.match(/(?:dp\/|gp\/product\/|ASIN\/|\/([A-Z0-9]{10})(?:\/|\?|$))|^([A-Z0-9]{10})$/i);
      if (asinMatch) {
        asinValue = (asinMatch[1] || asinMatch[2] || asinValue).toUpperCase();
      }
      // Si toujours une URL complète, extraire le pattern dp/XXXXX
      if (asinValue.includes('/dp/')) {
        const dpMatch = asinValue.match(/\/dp\/([A-Z0-9]{10})/i);
        if (dpMatch) asinValue = dpMatch[1].toUpperCase();
      }
      
      // Calculer la réduction si prix barré fourni
      const currentPrice = parseFloat(manualForm.price) || 0;
      const originalPrice = parseFloat(manualForm.originalPrice) || currentPrice;
      let savingPercent = 0;
      let savingAmount = 0;
      if (originalPrice > currentPrice && currentPrice > 0) {
        savingAmount = originalPrice - currentPrice;
        savingPercent = Math.round((savingAmount / originalPrice) * 100);
      }
      
      // Parser les caractéristiques (une par ligne)
      const features = manualForm.features
        .split('\n')
        .map(f => f.trim())
        .filter(f => f.length > 0);
      
      // Appeler l'endpoint avec toutes les infos manuelles
      const response = await axios.post(`${API}/api/amazon/manual-generate`, {
        asin: asinValue,
        site_id: selectedSiteId,
        title: manualForm.title.trim(),
        brand: manualForm.brand.trim() || null,
        current_price: currentPrice,
        original_price: originalPrice,
        saving_percent: savingPercent,
        image_url: manualForm.imageUrl.trim() || null,
        features: features,
        publish_immediately: true
      });
      
      if (response.data.published) {
        toast.success(`Article publié avec succès ! ID: ${response.data.wordpress_id}`);
        // Reset le formulaire
        setManualForm({
          title: '',
          price: '',
          originalPrice: '',
          imageUrl: '',
          brand: '',
          features: '',
          asin: ''
        });
        fetchPublished();
      } else if (response.data.article_id) {
        toast.success('Article généré avec succès !');
      } else {
        toast.error('Erreur lors de la génération');
      }
    } catch (error) {
      console.error('Full manual generate error:', error);
      const detail = error.response?.data?.detail || 'Erreur lors de la génération';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // Générer et publier l'article
  const handlePublishManual = async () => {
    if (!manualProduct || !selectedSiteId) {
      toast.error('Sélectionnez un produit et un site');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/amazon/from-url`, {
        url: manualProduct.asin,
        site_id: selectedSiteId,
        publish_immediately: true
      });
      
      if (response.data.published) {
        toast.success(`Article publié ! ID: ${response.data.wordpress_id}`);
        setManualProduct(null);
        setManualUrl('');
        fetchPublished();
      } else {
        toast.error('Erreur lors de la publication');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de publication');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerNow = async (siteKey) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/amazon/trigger-now`, null, {
        params: { site_key: siteKey }
      });
      toast.success(`Article publié : ${response.data.product?.substring(0, 50)}...`);
      fetchStatus();
      fetchPublished();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de publication');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateArticle = async (asin, siteId, publishImmediately = false) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/amazon/generate`, {
        asin,
        site_id: siteId,
        publish_immediately: publishImmediately
      });
      
      if (response.data.published) {
        toast.success(`Article publié ! ID: ${response.data.wordpress_id}`);
        fetchPublished();
      } else {
        toast.success('Article généré avec succès');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de génération');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(price);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ShoppingCart className="h-8 w-8 text-sky-400" />
            Amazon Deals
          </h1>
          <p className="text-gray-400 mt-1">
            Génération automatique d'articles promotionnels avec liens affiliés
          </p>
        </div>
        <Button onClick={fetchStatus} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Status Cards */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-sky-500/20 to-sky-600/10 border-sky-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">API Status</p>
                  <p className="text-2xl font-bold">
                    {status.authenticated ? (
                      <span className="text-green-400">Connecté</span>
                    ) : (
                      <span className="text-red-400">Déconnecté</span>
                    )}
                  </p>
                </div>
                <Zap className={`h-8 w-8 ${status.authenticated ? 'text-green-400' : 'text-red-400'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Tag Partenaire</p>
                  <p className="text-xl font-mono font-bold text-sky-400">{status.partner_tag}</p>
                </div>
                <Package className="h-8 w-8 text-sky-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Publiés aujourd'hui</p>
                  <p className="text-2xl font-bold">{status.published_today}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total publié</p>
                  <p className="text-2xl font-bold">{status.total_published}</p>
                </div>
                <TrendingDown className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedules */}
      {status?.schedules && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configuration des publications automatiques
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {status.schedules.map((schedule) => (
                <div
                  key={schedule.key}
                  className="p-4 rounded-lg bg-slate-900/50 border border-slate-600"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg">{schedule.site_name}</h3>
                      <p className="text-sm text-gray-400">{schedule.category_name}</p>
                    </div>
                    <Badge variant={schedule.enabled ? 'success' : 'secondary'}>
                      {schedule.enabled ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-300 mb-3">
                    <span>⏰ {schedule.start_hour}h - {schedule.end_hour}h</span>
                    <span>📅 Toutes les {schedule.interval_hours}h</span>
                  </div>
                  
                  <Button
                    onClick={() => handleTriggerNow(schedule.key)}
                    disabled={loading || !schedule.site_id}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Publier maintenant
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Saisie Manuelle - Mode principal tant que l'API n'est pas activée */}
      <Card className="bg-gradient-to-br from-sky-500/10 to-sky-600/10 border-sky-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-sky-400" />
            Créer un article Amazon
          </CardTitle>
          <CardDescription>
            Générez automatiquement un article promotionnel avec lien affilié
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Sélecteur de mode */}
          <div className="flex gap-2 p-1 bg-slate-800 rounded-lg w-fit">
            <button
              onClick={() => { setInputMode('url'); setShowManualForm(false); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                inputMode === 'url' 
                  ? 'bg-sky-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Link2 className="h-4 w-4" />
              Via URL / ASIN
            </button>
            <button
              onClick={() => { setInputMode('full_manual'); setShowManualForm(true); setManualProduct(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                inputMode === 'full_manual' 
                  ? 'bg-sky-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Edit className="h-4 w-4" />
              Saisie 100% manuelle
            </button>
          </div>
          
          {/* Mode URL/ASIN */}
          {inputMode === 'url' && (
            <div className="flex flex-wrap gap-4">
              <Input
                placeholder="URL Amazon ou ASIN (ex: https://www.amazon.fr/dp/B0XXXXXX ou B0XXXXXX)"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                className="flex-1 min-w-[300px]"
                onKeyDown={(e) => e.key === 'Enter' && handleFetchProduct()}
                data-testid="amazon-url-input"
              />
              
              <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                <SelectTrigger className="w-[200px]" data-testid="amazon-site-select">
                  <SelectValue placeholder="Site de destination" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button onClick={handleFetchProduct} disabled={loading || !manualUrl.trim()} data-testid="amazon-fetch-btn">
                {loading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Récupérer
              </Button>
            </div>
          )}

          {/* Formulaire de saisie manuelle (mode full_manual OU si scraping URL échoue) */}
          {showManualForm && (
            <div className="p-4 rounded-lg bg-sky-900/20 border border-sky-500/30 mt-4">
              <h4 className="font-bold text-sky-400 mb-3">
                {inputMode === 'full_manual' ? '📝 Saisie manuelle complète' : '⚠️ Saisie manuelle des informations'}
              </h4>
              <p className="text-sm text-gray-400 mb-4">
                {inputMode === 'full_manual' 
                  ? 'Remplissez les informations du produit. Copiez-collez depuis la page Amazon.'
                  : 'Amazon bloque le scraping automatique. Copiez-collez les infos depuis la page du produit :'
                }
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-300">Titre du produit *</label>
                  <Input
                    placeholder="Ex: Apple AirPods Pro (2ème génération) avec boîtier MagSafe"
                    value={manualForm.title}
                    onChange={(e) => setManualForm({...manualForm, title: e.target.value})}
                    className="mt-1"
                    data-testid="amazon-manual-title"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300">ASIN ou URL du produit *</label>
                  <Input
                    placeholder="Ex: B0D1XD1ZV3 ou https://amazon.fr/dp/..."
                    value={inputMode === 'full_manual' ? manualForm.asin : manualUrl}
                    onChange={(e) => inputMode === 'full_manual' 
                      ? setManualForm({...manualForm, asin: e.target.value})
                      : setManualUrl(e.target.value)
                    }
                    className="mt-1"
                    data-testid="amazon-manual-asin"
                  />
                  <p className="text-xs text-gray-500 mt-1">L'ASIN est le code à 10 caractères (ex: B0D1XD1ZV3)</p>
                </div>
                <div>
                  <label className="text-sm text-gray-300">Marque</label>
                  <Input
                    placeholder="Ex: Apple"
                    value={manualForm.brand}
                    onChange={(e) => setManualForm({...manualForm, brand: e.target.value})}
                    className="mt-1"
                    data-testid="amazon-manual-brand"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300">Prix actuel (€) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 279.00"
                    value={manualForm.price}
                    onChange={(e) => setManualForm({...manualForm, price: e.target.value})}
                    className="mt-1"
                    data-testid="amazon-manual-price"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300">Prix barré / original (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 299.00 (pour afficher la réduction)"
                    value={manualForm.originalPrice}
                    onChange={(e) => setManualForm({...manualForm, originalPrice: e.target.value})}
                    className="mt-1"
                    data-testid="amazon-manual-original-price"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-300">URL de l'image (clic droit sur l'image → Copier l'adresse)</label>
                  <Input
                    placeholder="https://m.media-amazon.com/images/I/..."
                    value={manualForm.imageUrl}
                    onChange={(e) => setManualForm({...manualForm, imageUrl: e.target.value})}
                    className="mt-1"
                    data-testid="amazon-manual-image"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-300">Caractéristiques principales (une par ligne)</label>
                  <Textarea
                    placeholder="Ex:&#10;Réduction active du bruit 2x plus efficace&#10;Audio spatial personnalisé&#10;Jusqu'à 6h d'écoute avec une seule charge"
                    value={manualForm.features}
                    onChange={(e) => setManualForm({...manualForm, features: e.target.value})}
                    className="mt-1 min-h-[100px]"
                    data-testid="amazon-manual-features"
                  />
                </div>
                
                {/* Site de destination en mode full_manual */}
                {inputMode === 'full_manual' && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-300">Site de destination *</label>
                    <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                      <SelectTrigger className="mt-1" data-testid="amazon-manual-site-select">
                        <SelectValue placeholder="Choisir le site WordPress" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              {inputMode === 'full_manual' ? (
                <Button
                  onClick={handleFullManualGenerate}
                  disabled={loading || !manualForm.title.trim() || !manualForm.price || !manualForm.asin.trim() || !selectedSiteId}
                  className="mt-4 bg-sky-600 hover:bg-sky-500 w-full"
                  data-testid="amazon-generate-btn"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Générer & Publier l'article
                </Button>
              ) : (
                <Button
                  onClick={handleApplyManualInfo}
                  disabled={loading || !manualForm.title.trim()}
                  className="mt-4 bg-sky-600 hover:bg-sky-500"
                  data-testid="amazon-apply-manual-btn"
                >
                  Appliquer les informations
                </Button>
              )}
            </div>
          )}

          {/* Aperçu du produit récupéré */}
          {manualProduct && (
            <div className="p-4 rounded-lg bg-slate-900/70 border border-sky-500/30 mt-4">
              <div className="flex gap-4">
                {manualProduct.image_url && (
                  <img
                    src={manualProduct.image_url}
                    alt={manualProduct.title}
                    className="w-32 h-32 object-contain rounded bg-white p-2"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-bold text-lg line-clamp-2">{manualProduct.title}</h3>
                  {manualProduct.brand && (
                    <p className="text-sm text-gray-400">{manualProduct.brand}</p>
                  )}
                  
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-2xl font-bold text-green-400">
                      {formatPrice(manualProduct.current_price)}
                    </span>
                    {manualProduct.saving_percent > 0 && (
                      <>
                        <span className="text-lg text-gray-500 line-through">
                          {formatPrice(manualProduct.original_price)}
                        </span>
                        <Badge variant="destructive" className="bg-red-600 text-lg px-3">
                          -{manualProduct.saving_percent}%
                        </Badge>
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 mt-2">
                    {manualProduct.rating > 0 && (
                      <span className="flex items-center gap-1 text-yellow-400">
                        <Star className="h-4 w-4 fill-current" />
                        {manualProduct.rating} ({manualProduct.review_count} avis)
                      </span>
                    )}
                    {manualProduct.is_prime && (
                      <Badge className="bg-blue-600">Prime</Badge>
                    )}
                    {manualProduct.source === 'scrape' && (
                      <Badge variant="outline" className="text-sky-400 border-sky-400">
                        Mode manuel
                      </Badge>
                    )}
                  </div>
                  
                  {manualProduct.features?.length > 0 && (
                    <ul className="mt-3 text-sm text-gray-300 space-y-1">
                      {manualProduct.features.slice(0, 3).map((f, i) => (
                        <li key={i} className="line-clamp-1">• {f}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 mt-4 pt-4 border-t border-slate-700">
                <a
                  href={manualProduct.affiliate_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Voir sur Amazon
                  </Button>
                </a>
                <Button
                  onClick={handlePublishManual}
                  disabled={loading || !selectedSiteId}
                  className="flex-1 bg-sky-600 hover:bg-sky-500"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Générer & Publier l'article
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search Deals */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle>Rechercher des deals (API)</CardTitle>
          <CardDescription>
            {status?.authenticated 
              ? "Trouvez les meilleures promotions High-Tech sur Amazon"
              : "⚠️ API non activée - Utilisez le mode manuel ci-dessus en attendant les 10 ventes"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            <Input
              placeholder="Mots-clés (ex: smartphone, casque bluetooth...)"
              value={searchKeywords}
              onChange={(e) => setSearchKeywords(e.target.value)}
              className="flex-1 min-w-[200px]"
            />
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categories).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={String(minSaving)} onValueChange={(v) => setMinSaving(Number(v))}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Réduction min" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">-5% minimum</SelectItem>
                <SelectItem value="10">-10% minimum</SelectItem>
                <SelectItem value="20">-20% minimum</SelectItem>
                <SelectItem value="30">-30% minimum</SelectItem>
                <SelectItem value="50">-50% minimum</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={fetchDeals} disabled={loading}>
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Rechercher
            </Button>
          </div>

          {/* Deals Grid */}
          {deals.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {deals.map((product) => (
                <div
                  key={product.asin}
                  className="p-4 rounded-lg bg-slate-900/50 border border-slate-600 hover:border-sky-500/50 transition-colors"
                >
                  {/* Product Image */}
                  {product.image_url && (
                    <div className="mb-3 text-center">
                      <img
                        src={product.image_url}
                        alt={product.title}
                        className="h-32 mx-auto object-contain"
                      />
                    </div>
                  )}
                  
                  {/* Title & Brand */}
                  <h4 className="font-medium text-sm line-clamp-2 mb-1">{product.title}</h4>
                  {product.brand && (
                    <p className="text-xs text-gray-400 mb-2">{product.brand}</p>
                  )}
                  
                  {/* Price & Saving */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-bold text-green-400">
                      {formatPrice(product.current_price)}
                    </span>
                    {product.saving_percent > 0 && (
                      <>
                        <span className="text-sm text-gray-500 line-through">
                          {formatPrice(product.original_price)}
                        </span>
                        <Badge variant="destructive" className="bg-red-600">
                          -{product.saving_percent}%
                        </Badge>
                      </>
                    )}
                  </div>
                  
                  {/* Rating & Prime */}
                  <div className="flex items-center gap-2 mb-3 text-sm">
                    {product.rating > 0 && (
                      <span className="flex items-center gap-1 text-yellow-400">
                        <Star className="h-4 w-4 fill-current" />
                        {product.rating}
                      </span>
                    )}
                    {product.is_prime && (
                      <Badge className="bg-blue-600 text-xs">Prime</Badge>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <a
                      href={product.affiliate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" size="sm" className="w-full">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Amazon
                      </Button>
                    </a>
                    
                    {status?.schedules?.[0]?.site_id && (
                      <Button
                        size="sm"
                        onClick={() => handleGenerateArticle(product.asin, status.schedules[0].site_id, true)}
                        disabled={loading}
                        className="bg-sky-600 hover:bg-sky-500"
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Publier
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Published Articles */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle>Articles publiés récemment</CardTitle>
        </CardHeader>
        <CardContent>
          {published.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Aucun article publié pour le moment</p>
          ) : (
            <div className="space-y-2">
              {published.map((article, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded bg-slate-900/50"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm line-clamp-1">{article.title}</p>
                    <p className="text-xs text-gray-400">
                      {article.site_name} • {new Date(article.published_at).toLocaleDateString('fr-FR')}
                      {article.saving_percent > 0 && ` • -${article.saving_percent}%`}
                    </p>
                  </div>
                  {article.wordpress_url && (
                    <a
                      href={article.wordpress_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AmazonDeals;
